/**
 * Computes the min/max boundary boxes for simple mode resizing.
 * maxBox: how far the box edges can expand outward from the original.
 * minBox: how far the box edges can shrink inward from the original.
 */
export function getSimpleModeBounds(originalBox, resizeMarginPercent) {
  const edgeMargin = originalBox.width * (resizeMarginPercent / 100);
  const edgeMarginY = originalBox.height * (resizeMarginPercent / 100);
  return {
    maxBox: {
      x1: originalBox.x - edgeMargin,
      y1: originalBox.y - edgeMarginY,
      x2: originalBox.x + originalBox.width + edgeMargin,
      y2: originalBox.y + originalBox.height + edgeMarginY
    },
    minBox: {
      x1: originalBox.x + edgeMargin,
      y1: originalBox.y + edgeMarginY,
      x2: originalBox.x + originalBox.width - edgeMargin,
      y2: originalBox.y + originalBox.height - edgeMarginY
    }
  };
}

/**
 * Clamps a blur box within image boundaries and size constraints.
 */
export function clampBox(
  box,
  {
    imageDimensions,
    isSimpleMode,
    originalBox,
    simpleResizeMargin,
    maxBoxWidth,
    maxBoxHeight,
    maxPositionRatio
  }
) {
  box = { ...box };

  if (isSimpleMode && originalBox) {
    const { maxBox, minBox } = getSimpleModeBounds(
      originalBox,
      simpleResizeMargin
    );
    const minW = minBox.x2 - minBox.x1;
    const minH = minBox.y2 - minBox.y1;
    box.width = Math.max(minW, Math.min(maxBox.x2 - maxBox.x1, box.width));
    box.height = Math.max(minH, Math.min(maxBox.y2 - maxBox.y1, box.height));
    box.x = Math.max(maxBox.x1, Math.min(maxBox.x2 - box.width, box.x));
    box.y = Math.max(maxBox.y1, Math.min(maxBox.y2 - box.height, box.y));
  }

  box.width = Math.min(box.width, maxBoxWidth);
  box.height = Math.min(box.height, maxBoxHeight);

  box.x = Math.max(0, box.x);
  box.y = Math.max(0, box.y);

  if (imageDimensions) {
    const maxY = imageDimensions.height * maxPositionRatio;
    if (box.y + box.height > maxY) {
      box.y = maxY - box.height;
    }
    if (box.y < 0) {
      box.y = 0;
      box.height = maxY;
    }
    if (box.x + box.width > imageDimensions.width) {
      box.x = imageDimensions.width - box.width;
    }
  }

  box.x = Math.round(box.x);
  box.y = Math.round(box.y);
  box.width = Math.round(box.width);
  box.height = Math.round(box.height);

  return box;
}
